import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	createShareLink,
	revokeShareLink,
	fetchShareLinks,
} from 'slices/documentLibrarySlice'
import type {
	CreateShareLinkResponse,
	DocumentLibraryState,
	DocumentShareLink,
	RevokeShareLinkResponse,
} from 'features/DocumentLibrary/types/documentLibrary.types'

interface UseShareLinkResult {
	links: DocumentShareLink[]
	createLink: (args: {
		documentId: string
		versionId?: string
		ttlDays?: number
		email?: string
		message?: string
	}) => Promise<CreateShareLinkResponse>
	revokeLink: (args: {
		token: string
		documentId: string
	}) => Promise<RevokeShareLinkResponse>
	refresh: (documentId: string) => Promise<unknown>
}

/**
 * Thin wrapper around the share-link thunks. Returns the cached links
 * for `documentId` plus stable callbacks for create/revoke/refresh.
 *
 * Toasts are intentionally NOT raised here — the dialog component
 * decides whether to show them, since the same thunks are used from
 * library-list-row "Share" clicks where a different UX may apply.
 */
export const useShareLink = (
	documentId: string | null | undefined,
): UseShareLinkResult => {
	const dispatch = useDispatch<any>()
	const links = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			documentId
				? state.libraryDocuments.sharesByDocument[documentId] || []
				: [],
	)

	const createLink = useCallback(
		(args: {
			documentId: string
			versionId?: string
			ttlDays?: number
			email?: string
			message?: string
		}) => dispatch(createShareLink(args)).unwrap(),
		[dispatch],
	)

	const revokeLink = useCallback(
		(args: { token: string; documentId: string }) =>
			dispatch(revokeShareLink(args)).unwrap(),
		[dispatch],
	)

	const refresh = useCallback(
		(id: string) => dispatch(fetchShareLinks({ documentId: id })).unwrap(),
		[dispatch],
	)

	return { links, createLink, revokeLink, refresh }
}
